/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { JSX } from "react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../../global/emotion";
import { radius, spacing } from "../../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import { Text } from "../../../_core/Text/Text.emotion";
import { QuoteIcon } from "../../../icons/Icon/GeneralIcons";

const hallowElement: HallowElement = "div";

const actionsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};
const attributionStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  overflow: "hidden",
  textAlign: "center",
  textOverflow: "ellipsis",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  width: spacing.full,
});
const containerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.ms,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  justifyContent: "center",
  paddingInline: spacing.l,
  [cssMediaQueryMinWidthTablet]: { paddingInline: spacing.xxl },
  paddingBlock: spacing.xl,
  width: spacing.full,
});
const iconContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  padding: spacing.xs,
};
const quoteContainerStyle: Interpolation = {
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  flex: "1 0 0",
  flexDirection: "column",
  gap: spacing.s,
  justifyContent: "center",
};
const quoteTextStyle: Interpolation = {
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 6,
  display: "-webkit-box",
  maxWidth: spacing.maxWidth,
  overflow: "hidden",
  textAlign: "center",
  textOverflow: "ellipsis",
  width: spacing.full,
};

export type DailyQuoteProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  actions?: JSX.Element[];
  attribution?: string;
  quote: string;
};

export const DailyQuote = forwardRef<any, DailyQuoteProps>(
  (
    { actions, asChild = false, attribution, children, quote, ...props },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <span key="iconContainer" css={iconContainerStyle}>
          <QuoteIcon />
        </span>,
        <div key="quoteContainer" css={quoteContainerStyle}>
          {attribution && (
            <Text type="detail" size="l" css={attributionStyle}>
              {attribution}
            </Text>
          )}
          <Text type="quote" size="xl" as={"blockquote"} css={quoteTextStyle}>
            {quote}
          </Text>
        </div>,
        actions?.length > 0 ? (
          <div key="actions" css={actionsStyle}>
            {actions}
          </div>
        ) : null,
      ],
      children: children as JSX.Element,
    });
    return (
      <DeterminedElement ref={ref} css={containerStyle} {...props}>
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

DailyQuote.displayName = "DailyQuote";
